import { toJson, type JsonObject, type JsonValue } from "@bufbuild/protobuf";
import { ValueSchema } from "@bufbuild/protobuf/wkt";
import { faker } from "@faker-js/faker";
import { SchemaPropertyType, type ContactSchema, type SchemaProperty } from "#gen/abacus/v2/abacus_pb.js";
import { BundleType, ResponseType, type Agent, type AssetRef, type DataSet, type Lookup, type MappedValue } from "#gen/bundles/v1/bundle_pb.js";
import type { Batch } from "#gen/codes/v3/codes_pb.js";
import { DataType } from "#gen/common/v1/types_pb.js";
import type { EntrypointBatch } from "#gen/entrypoints/v1/entrypoints_pb.js";
import { ChoiceType, FieldType, type Field } from "#gen/forms/v1/field_pb.js";
import type { Page } from "#gen/pages/v2/page_pb.js";
import { X_FLOWCODE_DATA_TYPE_KEY, dataTypeForSchema } from "#lib/components/flow/editor/data-types/dataTypes.js";
import type { Schema } from "#lib/components/jsonForms/jsonSchema.js";
import { deepClone } from "#lib/deepClone.js";
import { getFormFieldsOnPage } from "#lib/form.js";
import { Provider } from "#lib/provider.js";
import { sentenceCase, titleCase } from "#lib/string.js";
import { Trie, type Suggestion } from "#lib/trie.js";

export { Provider };

interface Token {
	label: string;
	value: string;
	index?: number;
	data?: object;
}

export interface SubstitutionToken extends Token {
	dataType: DataType;
	provider: Provider;
	providerName?: string;
	objectPath: string;
	description?: string;
	placeholder?: JsonValue;
	data?: Field | Schema | SchemaProperty;
}

type TextToken = Token;

export class TokenProvider {
	private trie: Trie<SubstitutionToken>;
	private values: Map<string, SubstitutionToken>;

	constructor() {
		this.trie = new Trie<SubstitutionToken>();
		this.values = new Map<string, SubstitutionToken>();
	}

	exceptProvider(...providers: Provider[]): TokenProvider {
		const provider = new TokenProvider();
		for (const token of this.tokens()) {
			if (!providers.includes(token.provider)) {
				provider.addToken(token.label, token);
			}
		}
		return provider;
	}

	withTokensOfType(...types: DataType[]): TokenProvider {
		const provider = new TokenProvider();
		for (const type of types) {
			for (const token of this.tokensByType(type)) {
				provider.addToken(token.label, token);
			}
		}
		return provider;
	}

	hasTokensOfType(type: DataType): boolean {
		return this.tokensByType(type).length > 0;
	}

	addDataElementsForType(type: BundleType, datasets: Record<string, DataSet> = {}): TokenProvider {
		switch (type) {
			case BundleType.REAL_ESTATE:
				this.populateRealEstateTokens();
				break;
			case BundleType.COMMERCE:
				this.populateCommerceTokens();
				break;
			case BundleType.FORM:
				this.populateFormTokens();
				break;
			case BundleType.CONTACT:
				this.populateContactTokens();
				break;
		}
		for (const key in datasets) {
			const dataset = datasets[key];
			if (dataset.lookup) {
				this.populateLookupTokens(dataset.name, `dataset.${key}`, dataset.lookup);
			}
		}
		return this;
	}

	addMetadataElements(batch: Batch | EntrypointBatch, metadata?: Schema, external: boolean = false): TokenProvider {
		if (!metadata) {
			return this;
		}
		this.populateMetadataTokens(batch, metadata, external);
		return this;
	}

	addPageDataElements(ref: AssetRef, page: Page, data?: JsonObject): TokenProvider {
		if (!data) {
			return this;
		}
		this.populatePageTokens(ref, page, data);
		return this;
	}

	addAgentElements(ref: AssetRef, agent: Agent): TokenProvider {
		this.populateAgentTokens(ref, agent);
		return this;
	}

	addLookupElements(ref: AssetRef, lookup: Lookup): TokenProvider {
		this.populateLookupTokens(ref.displayName, "requestData", lookup);
		return this;
	}

	addContactProperties(schema?: ContactSchema): TokenProvider {
		if (!schema) {
			return this;
		}
		this.populateContactProperties(schema);
		return this;
	}

	addEventProperties(eventProperties: Record<string, DataType>): TokenProvider {
		this.populateEventProperties(eventProperties ?? {});
		return this;
	}

	token(value: string): SubstitutionToken | undefined {
		return this.values.get(this.normalize(value));
	}

	suggest(initials: string): Suggestion<SubstitutionToken>[] {
		return this.trie.suggest(initials.toLowerCase());
	}

	tokens(): SubstitutionToken[] {
		return Array.from(this.values.values());
	}

	tokensByProvider(provider: string): SubstitutionToken[] {
		return this.tokens().filter(token => token.provider === provider);
	}

	tokensByType(type: DataType): SubstitutionToken[] {
		return this.tokens().filter(token => token.dataType === type);
	}

	parse(value: string): Token[] {
		const tokens = [];
		let i: number;
		let end = 0;
		for (i = 0; i < value.length;) {
			const start = value.indexOf("{{", i);
			if (start < 0) {
				break;
			}
			end = value.indexOf("}}", start + 2);
			if (end < 0) {
				break;
			}
			if (start > i) {
				const text = value.substring(i, start);
				tokens.push({ label: text, value: text });
			}
			const text = value.substring(start, end + 2);
			const token = this.token(text);
			if (token) {
				if (tokens.length === 0 || this.instanceofSubstitutionToken(tokens[tokens.length - 1])) {
					tokens.push(this.newEmptyTextToken());
				}
				token.index = start;
				tokens.push(token);
			} else {
				const invalid = this.newInvalidToken(text);
				invalid.index = start;
				tokens.push(invalid);
			}
			i = end + 2;
		}
		if (i < value.length) {
			const text = value.substring(end ? i : 0);
			tokens.push({ label: text, value: text });
		} else {
			tokens.push(this.newEmptyTextToken());
		}
		return tokens;
	}

	instanceofSubstitutionToken(token: Token): token is SubstitutionToken {
		return Object.hasOwn(token, "provider");
	}

	getSubstitutionToken(value: string): SubstitutionToken | undefined {
		return this.token(value);
	}

	hasSubstitutionToken(value: string | null | undefined): boolean {
		if (!value) {
			return false;
		}
		const tokens = this.parse(value);
		return tokens.some(token => this.instanceofSubstitutionToken(token));
	}

	newEmptyTextToken(): TextToken {
		return { label: "", value: "" };
	}

	newInvalidToken(value: string): SubstitutionToken {
		return { provider: Provider.INVALID, label: "Invalid token", value } as SubstitutionToken;
	}

	clone(): TokenProvider {
		const clone = new TokenProvider();
		clone.trie = this.trie.clone();
		clone.values = deepClone(this.values);
		return clone;
	}

	private populateMetadataTokens(batch: Batch | EntrypointBatch, metadata: Schema, external: boolean) {
		Object.entries(metadata.properties as Record<string, Schema>).forEach(([key, value]) => {
			const dataType = this.dataTypeFromSchema(value);
			this.addToken(value.title ?? key, {
				label: value.title ?? key,
				provider: Provider.CODE,
				providerName: `Data properties${external ? ` (${batch.name})` : ""}`,
				dataType: dataType,
				description: value.description,
				objectPath: key,
				value: this.valueForDataType(dataType, `.request.${key}`),
				placeholder: batch.metadata?.[key] as JsonValue,
				data: value,
			});
		});
	}

	private populatePageTokens(ref: AssetRef, page: Page, data: JsonObject) {
		const formFields = getFormFieldsOnPage(page, data);
		for (const [id, fields] of Object.entries(formFields)) {
			for (const field of fields) {
				const path = `.requestData.${id}.${field.name}`;
				const dataType = this.dataTypeFromField(field);
				const value = field.fieldType === FieldType.FILE_UPLOAD ? `{{ (index ${path} 0).url }}` : this.valueForDataType(dataType, path);
				this.addToken(field.title, {
					label: field.title,
					provider: Provider.FORM,
					dataType: dataType,
					description: `The submitted value of the "${field.title}" field`,
					providerName: `${ref.displayName} form`,
					objectPath: `${id}.${field.name}`,
					data: field,
					placeholder: field.defaultValue,
					value,
				});
			}
		}
	}

	private populateAgentTokens(ref: AssetRef, agent: Agent) {
		if (agent.responseType === ResponseType.IMAGE) {
			this.addToken("image", {
				label: "Image URL",
				provider: Provider.AGENT,
				dataType: DataType.URL,
				description: `The image generated by the agent`,
				providerName: "agent",
				objectPath: "",
				value: "{{ .responseData.url }}",
			});
		} else if (agent.responseType === ResponseType.TEXT) {
			this.addToken("text", {
				label: "Text Response",
				provider: Provider.AGENT,
				dataType: DataType.STRING,
				description: `The text generated by the agent`,
				providerName: `${ref.displayName} agent`,
				objectPath: "",
				value: "{{ .responseData.text }}",
			});
		}
	}

	private populateContactProperties(schema: ContactSchema) {
		schema.properties.forEach(value => {
			const dataType = this.dataTypeFromContactProperty(value);
			this.addToken(value.key, {
				label: value.label,
				provider: Provider.CONTACT_PROPERTY,
				providerName: "Contact properties",
				dataType: dataType,
				description: `The identified contact's "${value.label}" property`,
				objectPath: value.key,
				value: this.valueForDataType(dataType, `.contact.${value.key}`),
				data: value,
			});
		});
	}

	private populateLookupTokens(label: string, key: string, lookup: Lookup) {
		let mapping: MappedValue[] = [];
		if (lookup.config.case === "apiLookup") {
			mapping = lookup.config.value?.response?.bodyMapping ?? [];
		} else if (lookup.config.case === "csvLookup") {
			mapping = lookup.config.value?.columnMapping ?? [];
		}
		Object.values(mapping).forEach(value => {
			this.addToken(value.key, {
				label: titleCase(value.key),
				provider: Provider.LOOKUP,
				providerName: `${label} lookup`,
				dataType: value.dataType,
				description: `The value of the "${value.key}" field from the lookup result`,
				objectPath: value.key,
				value: this.valueForDataType(value.dataType, `.${key}.${value.key}`),
				placeholder: value.sampleValue ? toJson(ValueSchema, value.sampleValue) : undefined,
				data: value,
			});
		});
	}

	private populateEventProperties(eventProperties: Record<string, DataType>) {
		for (const [property, dataType] of Object.entries(eventProperties)) {
			this.addToken(property, { label: sentenceCase(property), provider: Provider.EVENT, dataType, description: `The value of the '${property}" event property`, objectPath: property, value: this.valueForDataType(dataType, `.event.${property}`) });
		}
		this.addToken("calendar", { label: "Calendar", provider: Provider.EVENT, dataType: DataType.STRING, description: "The calendar this event is on", objectPath: "calendar", value: "{{ .event.calendar }}" });
		this.addToken("name", { label: "Name", provider: Provider.EVENT, dataType: DataType.STRING, description: "The name of the event", objectPath: "title", value: "{{ .event.title }}" });
		this.addToken("description", { label: "Description", provider: Provider.EVENT, dataType: DataType.STRING, description: "The description of the event", objectPath: "description", value: "{{ .event.description }}" });
		this.addToken("image", { label: "Image", provider: Provider.EVENT, dataType: DataType.URL, description: "The image associated with the event", objectPath: "image", value: "{{ .event.image }}" });
		this.addToken("start_time", { label: "Start time", provider: Provider.EVENT, dataType: DataType.DATE, description: "The start time of the event", objectPath: "start_time", value: "{{ .event.start_time }}" });
		this.addToken("end_time", { label: "End time", provider: Provider.EVENT, dataType: DataType.DATE, description: "The end time of the event", objectPath: "end_time", value: "{{ .event.end_time }}" });
		this.addToken("timezone", { label: "Timezone", provider: Provider.EVENT, dataType: DataType.STRING, description: "The timezone of the event", objectPath: "timezone", value: "{{ .event.timezone }}" });
	}

	private populateRealEstateTokens() {
		this.addToken("mls id", { label: "Listing ID", provider: Provider.CONTENT, dataType: DataType.STRING, description: "The id of the listing, usually an MLS ID", objectPath: "realEstate.listing.mlsId", value: "{{ .data.realEstate.listing.mlsId }}" });
		this.addToken("url", { label: "URL", provider: Provider.CONTENT, dataType: DataType.URL, description: "The URL of the listing", objectPath: "realEstate.listing.url", value: "{{ .data.realEstate.listing.url }}" });
		this.addToken("description", { label: "Description", provider: Provider.CONTENT, dataType: DataType.STRING, description: "The description of the listing", objectPath: "realEstate.listing.description", value: "{{ .data.realEstate.listing.description }}" });
		this.addToken("photo", { label: "Primary photo", provider: Provider.CONTENT, dataType: DataType.URL, description: "The primary photo of the listing", objectPath: "realEstate.listing.photos[0]", value: "{{ index .data.realEstate.listing.photos 0 }}" });
		this.addToken("photos", { label: "Photos", provider: Provider.CONTENT, dataType: DataType.ARRAY, description: "The array of listing photos", objectPath: "realEstate.listing.photos", value: "{{ toImages .data.realEstate.listing.photos }}" });
		this.addToken("address", { label: "Address", provider: Provider.CONTENT, dataType: DataType.STRING, description: "The formatted address of the listing", objectPath: "realEstate.listing.address", value: "{{ formatAddress .data.realEstate.listing.address }}" });
		this.addToken("street 1", { label: "Street line 1", provider: Provider.CONTENT, dataType: DataType.STRING, description: "The first line of the street address", objectPath: "realEstate.listing.address.addressLines[0]", value: "{{ index .data.realEstate.listing.address.addressLines 0 }}" });
		this.addToken("street 2", { label: "Street line 2", provider: Provider.CONTENT, dataType: DataType.STRING, description: "The second line of the street address", objectPath: "realEstate.listing.address.addressLines[1]", value: "{{ index .data.realEstate.listing.address.addressLines 1 }}" });
		this.addToken("city", { label: "City", provider: Provider.CONTENT, dataType: DataType.STRING, description: "The city the listing is in", objectPath: "realEstate.listing.address.locality", value: "{{ .data.realEstate.listing.address.locality }}" });
		this.addToken("state", { label: "State", provider: Provider.CONTENT, dataType: DataType.STRING, description: "The state the listing is in", objectPath: "realEstate.listing.address.administrativeArea", value: "{{ .data.realEstate.listing.address.administrativeArea }}" });
		this.addToken("zip", { label: "Zip code", provider: Provider.CONTENT, dataType: DataType.STRING, description: "The zip code the listing is in", objectPath: "realEstate.listing.address.postalCode", value: "{{ .data.realEstate.listing.address.postalCode }}" });
		this.addToken("price", { label: "Price", provider: Provider.CONTENT, dataType: DataType.STRING, description: "The formatted price of the listing", objectPath: "realEstate.listing.price", value: "{{ formatMoney .data.realEstate.listing.price }}" });
		this.addToken("price units", { label: "Price units", provider: Provider.CONTENT, dataType: DataType.STRING, description: "The price units of the listing", objectPath: "realEstate.listing.price.units", value: "{{ .data.realEstate.listing.price.units }}" });
		this.addToken("agent first name", { label: "Agent first name", provider: Provider.CONTENT, dataType: DataType.STRING, description: "The first name of the listing agent", objectPath: "realEstate.agent.firstName", value: "{{ .data.realEstate.agent.firstName }}" });
		this.addToken("agent last name", { label: "Agent last name", provider: Provider.CONTENT, dataType: DataType.STRING, description: "The last name of the listing agent", objectPath: "realEstate.agent.lastName", value: "{{ .data.realEstate.agent.lastName }}" });
		this.addToken("agent email", { label: "Agent email", provider: Provider.CONTENT, dataType: DataType.EMAIL, description: "The email of the listing agent", objectPath: "realEstate.agent.email", value: "{{ .data.realEstate.agent.email }}" });
		this.addToken("agent phone", { label: "Agent phone", provider: Provider.CONTENT, dataType: DataType.PHONE, description: "The phone number of the listing agent", objectPath: "realEstate.agent.mobilePhoneNumber", value: "{{ .data.realEstate.agent.mobilePhoneNumber }}" });
		this.addToken("agent photo", { label: "Agent photo", provider: Provider.CONTENT, dataType: DataType.URL, description: "The photo of the listing agent", objectPath: "realEstate.agent.photo", value: "{{ .data.realEstate.agent.photo }}" });
		this.addToken("brokerage name", { label: "Brokerage name", provider: Provider.CONTENT, dataType: DataType.STRING, description: "The name of the listing brokerage", objectPath: "realEstate.agent.office.brokerage", value: "{{ .data.realEstate.agent.office.brokerage }}" });
		this.addToken("full bathrooms", { label: "# Full bathrooms", provider: Provider.CONTENT, dataType: DataType.NUMBER, description: "The number of full bathrooms the listing has", objectPath: "realEstate.listing.fullBathrooms", value: "{{ .data.realEstate.listing.fullBathrooms }}" });
		this.addToken("half bathrooms", { label: "# Half bathrooms", provider: Provider.CONTENT, dataType: DataType.NUMBER, description: "The number of half bathrooms the listing has", objectPath: "realEstate.listing.halfBathrooms", value: "{{ .data.realEstate.listing.halfBathrooms }}" });
		this.addToken("bathrooms", { label: "# Bathrooms", provider: Provider.CONTENT, dataType: DataType.NUMBER, description: "The number of bathrooms the listing has", objectPath: "realEstate.listing.bathrooms", value: "{{ .data.realEstate.listing.bathrooms }}" });
		this.addToken("bedrooms", { label: "# Bedrooms", provider: Provider.CONTENT, dataType: DataType.NUMBER, description: "The number of bedrooms the listing has", objectPath: "realEstate.listing.bedrooms", value: "{{ .data.realEstate.listing.bedrooms }}" });
		this.addToken("square feet", { label: "Square feet", provider: Provider.CONTENT, dataType: DataType.NUMBER, description: "The number of square footage of the listing", objectPath: "realEstate.listing.livingArea", value: "{{ .data.realEstate.listing.livingArea }}" });
		this.addToken("property type", { label: "Property type", provider: Provider.CONTENT, dataType: DataType.STRING, description: "The type of the property", objectPath: "realEstate.listing.propertyType", value: "{{ .data.realEstate.listing.propertyType }}" });
	}

	private populateCommerceTokens() {
		this.addToken("images", { label: "Images", provider: Provider.CONTENT, dataType: DataType.ARRAY, description: "The set of product images", objectPath: "commerce.product.images", value: "{{ array .data.commerce.product.images }}" });
		this.addToken("name", { label: "Name", provider: Provider.CONTENT, dataType: DataType.STRING, description: "The name of the product", objectPath: "commerce.product.title", value: "{{ .data.commerce.product.title }}" });
		this.addToken("description", { label: "Description", provider: Provider.CONTENT, dataType: DataType.STRING, description: "The description of the product", objectPath: "commerce.product.description", value: "{{ .data.commerce.product.description }}" });
		this.addToken("price", { label: "Price", provider: Provider.CONTENT, dataType: DataType.NUMBER, description: "The price of the product", objectPath: "commerce.product.priceRange.minVariantPrice", value: "{{ formatMoney .data.commerce.product.priceRange.minVariantPrice }}" });
	}

	private populateFormTokens() {
		this.addToken("images", { label: "Images", provider: Provider.CONTENT, dataType: DataType.ARRAY, description: "The set of images", objectPath: "form.images", value: "{{ array .data.form.images }}" });
		this.addToken("title", { label: "Title", provider: Provider.CONTENT, dataType: DataType.STRING, description: "The name of the primary form", objectPath: "form.name", value: "{{ .data.form.name }}" });
		this.addToken("description", { label: "Description", provider: Provider.CONTENT, dataType: DataType.STRING, description: "The description of the primary form", objectPath: "form.description", value: "{{ .data.form.description }}" });
	}

	private populateContactTokens() {
		this.addToken("photo", { label: "Photo", provider: Provider.CONTENT, dataType: DataType.URL, description: "The primary photo of the contact", objectPath: "contact.photo.url", value: "{{ .data.contact.photo.url }}" });
		this.addToken("full_name", { label: "Full name", provider: Provider.CONTENT, dataType: DataType.NAME, description: "The full name of the contact", objectPath: "contact.fullName", value: "{{ .data.contact.fullName }}" });
		this.addToken("title", { label: "Title", provider: Provider.CONTENT, dataType: DataType.STRING, description: "The title of the contact", objectPath: "contact.title", value: "{{ .data.contact.title }}" });
		this.addToken("department", { label: "Department", provider: Provider.CONTENT, dataType: DataType.STRING, description: "The department this contact is part of", objectPath: "contact.department", value: "{{ .data.contact.department }}" });
		this.addToken("position", { label: "Position", provider: Provider.CONTENT, dataType: DataType.STRING, description: "The position this contact holds", objectPath: "contact.position", value: '{{ join ", " .data.contact.title .data.contact.department }}' });
		this.addToken("company", { label: "Company", provider: Provider.CONTENT, dataType: DataType.STRING, description: "The company this contact works fort", objectPath: "contact.company", value: "{{ .data.contact.company }}" });
		this.addToken("bio", { label: "Bio", provider: Provider.CONTENT, dataType: DataType.STRING, description: "This contacts bio", objectPath: "contact.bio", value: "{{ .data.contact.bio }}" });
		this.addToken("work_number", { label: "Work number", provider: Provider.CONTENT, dataType: DataType.PHONE, description: "The work phone number for this contact", objectPath: "contact.workNumber", value: "{{ .data.contact.workNumber }}" });
		this.addToken("mobile_number", { label: "Mobile number", provider: Provider.CONTENT, dataType: DataType.PHONE, description: "The mobile phone number for this contact", objectPath: "contact.mobileNumber", value: "{{ .data.contact.mobileNumber }}" });
		this.addToken("email", { label: "Email", provider: Provider.CONTENT, dataType: DataType.EMAIL, description: "The email address of the contact", objectPath: "contact.email", value: "{{ .data.contact.email }}" });
		this.addToken("website", { label: "Website", provider: Provider.CONTENT, dataType: DataType.URL, description: "The website of the contact", objectPath: "contact.website", value: "{{ .data.contact.website }}" });
		this.addToken("address", { label: "Address", provider: Provider.CONTENT, dataType: DataType.ADDRESS, description: "The formatted address of the contact", objectPath: "contact.address", value: "{{ object .data.contact.address }}" });
		this.addToken("social_links", { label: "Social information", provider: Provider.CONTENT, dataType: DataType.ARRAY, description: "The social links of the contact", objectPath: "contact.socialLinks", value: "{{ array .data.contact.socialLinks }}" });
	}

	private addToken(key: string, token: SubstitutionToken) {
		this.trie.add(key, token);
		this.values.set(this.normalize(token.value), token);
	}

	private normalize(value: string): string {
		return value.replaceAll("{{", "").replaceAll("}}", "").trim();
	}

	private valueForDataType(dataType: DataType, path: string): string {
		switch (dataType) {
			case DataType.ADDRESS:
				return `{{ formatAddress ${path} }}`;
			case DataType.PHONE:
				return `{{ formatPhone ${path} }}`;
			case DataType.ARRAY:
				return `{{ join ", " ${path} }}`;
			case DataType.IMAGE:
				return `{{ ${path}.value }}`;
			default:
				return `{{ ${path} }}`;
		}
	}

	dataTypeFromSchema(schema: Schema): DataType {
		const xFlowcodeDataType = schema[X_FLOWCODE_DATA_TYPE_KEY];
		if (xFlowcodeDataType !== undefined && xFlowcodeDataType !== null) {
			return dataTypeForSchema(schema);
		}

		switch (schema.type) {
			case "integer":
			case "number":
				return DataType.NUMBER;
			case "string":
				return DataType.STRING;
			case "boolean":
				return DataType.BOOLEAN;
			case "array":
				return DataType.ARRAY;
			case "object":
				return DataType.OBJECT;
			default:
				return DataType.STRING;
		}
	}

	dataTypeFromField(field: Field): DataType {
		switch (field.fieldType) {
			case FieldType.EMAIL:
				return DataType.EMAIL;
			case FieldType.PHONE:
				return DataType.PHONE;
			case FieldType.NUMBER:
				return DataType.NUMBER;
			case FieldType.DATE:
				return DataType.DATE;
			case FieldType.ADDRESS:
				return DataType.ADDRESS;
			case FieldType.FILE_UPLOAD:
				return DataType.URL;
			case FieldType.CHOICE:
				if (field.properties?.properties?.case === "choice") {
					switch (field.properties.properties.value.choiceType) {
						case ChoiceType.BOOL:
							return DataType.BOOLEAN;
						case ChoiceType.CHECKBOX:
							return DataType.ARRAY;
						case ChoiceType.RADIO:
						case ChoiceType.DROPDOWN:
							return DataType.STRING;
					}
				}
				return DataType.ARRAY;
			case FieldType.NAME:
				return DataType.NAME;
			default:
				return DataType.STRING;
		}
	}

	dataTypeFromContactProperty(property: SchemaProperty): DataType {
		switch (property.type) {
			case SchemaPropertyType.NUMBER:
				return DataType.NUMBER;
			case SchemaPropertyType.STRING:
				return DataType.STRING;
			case SchemaPropertyType.BOOL:
				return DataType.BOOLEAN;
			case SchemaPropertyType.STRUCT:
			case SchemaPropertyType.OBJECT:
				return DataType.OBJECT;
			case SchemaPropertyType.DATE:
				return DataType.DATE;
			case SchemaPropertyType.URL:
				return DataType.URL;
			case SchemaPropertyType.LIST:
			case SchemaPropertyType.ARRAY:
				return DataType.ARRAY;
			case SchemaPropertyType.EMAIL:
				return DataType.EMAIL;
			case SchemaPropertyType.PHONE:
				return DataType.PHONE;
			case SchemaPropertyType.NAME_FULL:
				return DataType.NAME;
			case SchemaPropertyType.ADDRESS:
				return DataType.ADDRESS;
		}
		return DataType.STRING;
	}

	generateSampleValues(providers: Provider[] = Object.values(Provider)): JsonObject {
		const values: JsonObject = {};
		const providerSet = new Set(providers);
		for (const token of this.tokens().filter(token => providerSet.has(token.provider))) {
			const path = this.createObjectPath(token);
			if (token.placeholder !== undefined) {
				this.setValueAtPath(values, path, token.placeholder);
				continue;
			}
			switch (token.dataType) {
				case DataType.EMAIL:
					this.setValueAtPath(values, path, faker.internet.email());
					break;
				case DataType.PHONE:
					this.setValueAtPath(values, path, faker.phone.number({ style: "international" }));
					break;
				case DataType.URL:
					this.setValueAtPath(values, path, faker.image.url());
					break;
				case DataType.IMAGE:
					this.setValueAtPath(values, path, { value: faker.image.url(), contentType: "image/jpeg" });
					break;
				case DataType.NAME:
					this.setValueAtPath(values, path, faker.person.fullName());
					break;
				case DataType.NUMBER:
					this.setValueAtPath(values, path, faker.number.int({ min: 0, max: 1000 }));
					break;
				case DataType.BOOLEAN:
					this.setValueAtPath(values, path, true);
					break;
				case DataType.DATE:
					this.setValueAtPath(values, path, faker.date.anytime().toISOString());
					break;
				case DataType.ADDRESS:
					this.setValueAtPath(values, path, {
						addressLines: [faker.location.streetAddress(), faker.location.secondaryAddress()],
						locality: faker.location.city(),
						administrativeArea: faker.location.state(),
						postalCode: faker.location.zipCode(),
						regionCode: faker.location.country(),
					});
					break;
				case DataType.ARRAY:
					this.setValueAtPath(values, path, [`${token.label} item 1`, `${token.label} item 2`]);
					break;
				default:
					this.setValueAtPath(values, path, token.label);
			}
		}
		return values;
	}

	private createObjectPath(token: SubstitutionToken): string {
		let keyPrefix = "";
		switch (token.provider) {
			case Provider.CODE:
				keyPrefix = "request.";
				break;
			case Provider.FORM:
				keyPrefix = "requestData.";
				break;
			case Provider.CONTACT_PROPERTY:
				keyPrefix = "contact.";
				break;
			case Provider.CONTENT:
				keyPrefix = "data.";
				break;
			case Provider.AGENT:
				keyPrefix = "responseData.";
				break;
			case Provider.EVENT:
				keyPrefix = "event.";
				break;
			case Provider.LOOKUP:
				keyPrefix = "responseData.";
				break;
		}
		return `${keyPrefix}${token.objectPath}`;
	}

	private setValueAtPath(obj: JsonObject, path: string, value: JsonValue): void {
		const keys = path.split(".");
		let current: JsonObject = obj;
		for (let i = 0; i < keys.length - 1; i++) {
			const key = keys[i];
			if (!(key in current) || typeof current[key] !== "object" || current[key] === null) {
				current[key] = {};
			}
			current = current[key] as JsonObject;
		}
		const lastKey = keys[keys.length - 1];
		current[lastKey] = value;
	}
}
